import axios, { AxiosRequestConfig, Method } from 'axios';
import router from '@/router';
import { AUTH_TOKEN_STORAGE_KEY, AUTH_ROLE_STORAGE_KEY, AUTH_FIRST_NAME_STORAGE_KEY, AUTH_NEEDS_BROKERAGE_STORAGE_KEY } from '@/utils/authStorage';

// Defaults to the same host the page was loaded from (just a different port) so
// this works whether the app is opened via localhost, a LAN IP, or a Tailscale
// IP - a hardcoded "localhost" here would only ever work on the machine running
// the backend itself.
const defaultBaseUrl = `${window.location.protocol}//${window.location.hostname}:8860/commonsense`;

const client = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || defaultBaseUrl,
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
      localStorage.removeItem(AUTH_ROLE_STORAGE_KEY);
      localStorage.removeItem(AUTH_FIRST_NAME_STORAGE_KEY);
      localStorage.removeItem(AUTH_NEEDS_BROKERAGE_STORAGE_KEY);
      if (router.currentRoute.value.name !== 'login') {
        router.push({ name: 'login' });
      }
    }
    // The brokerage's subscription is suspended: everything is closed except the subscription page, where an admin can pay.
    if (error.response?.status === 402 && router.currentRoute.value.name !== 'abonnement') {
      router.push({ name: 'abonnement' });
    }
    // Signed in, but no brokerage chosen yet: send the user to the picker instead of showing errors.
    if (error.response?.status === 403 && error.response?.data === 'Brokerage not selected') {
      localStorage.setItem(AUTH_NEEDS_BROKERAGE_STORAGE_KEY, 'true');
      if (router.currentRoute.value.name !== 'kies-makelaar') {
        router.push({ name: 'kies-makelaar' });
      }
    }
    return Promise.reject(error);
  }
);

export async function request(method: Method, url: string, data?: any, config?: AxiosRequestConfig) {
  try {
    const response = await client.request({
      method,
      url,
      data,
      ...config
    });
    return response.data;
  } catch (error) {
    console.error(`Error making ${method} request:`, error);
    throw error;
  }
}
